import { all, call, put, select, takeLatest } from "typed-redux-saga";
import {
   createUserWithEmailAndPassword,
   deleteUser,
   sendEmailVerification,
   signInWithEmailAndPassword,
   signOut,
   updatePassword,
   updateProfile,
} from "firebase/auth";
import { deleteObject, getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { alertControls, isApiError, loaderControls } from "react-better-html";

import { type User } from "../../types/user";

import { type RootState } from "../store";
import { appSlice } from "../slices/app";
import { userSlice } from "../slices/user";

import { api } from "../../utils/api";
import { auth, signInWithPopupProvider, storageUserProfileImageRef } from "../../utils/firebase";
import { capturePosthogEvent, handleError, handleFirebaseError } from "../../utils/functions";
import { LocalStorage } from "../../utils/localStorage";
import { i18n } from "../../utils/i18n";

import { router } from "../../router";

function* onSignUpWithEmailAndPassword(action: ReturnType<typeof userSlice.actions.signUpWithEmailAndPassword>) {
   yield* call(() => loaderControls.startLoading("user:signUpWithEmailAndPassword"));

   try {
      const userCredential = yield* call(() =>
         createUserWithEmailAndPassword(auth, action.payload.email, action.payload.password),
      );

      const user = userCredential.user;

      yield* call(() =>
         updateProfile(user, {
            displayName: action.payload.fullName,
         }),
      );

      yield* put(userSlice.actions.setFirebaseUser(user));
      if (!user.emailVerified) yield* put(userSlice.actions.sendEmailVerification());

      yield* call(() =>
         finishRegister({
            fullName: action.payload.fullName,
         }),
      );

      action.payload.onSuccess?.();
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:signUpWithEmailAndPassword"));
   }
}

function* finishRegister(data: { fullName: string }) {
   const response = yield* call(() =>
      api("userRegister", {
         body: {
            fullName: data.fullName,
         },
      }),
   );

   yield* call(() => afterUserLoaded(response.data));

   yield* call(() =>
      alertControls.createAlert({
         type: "success",
         title: i18n.t((l) => l.api.success.default),
         message: i18n.t((l) => l.api.success.list.userRegistered),
      }),
   );
}

function* onLoginWithEmailAndPassword(action: ReturnType<typeof userSlice.actions.loginWithEmailAndPassword>) {
   yield* call(() => loaderControls.startLoading("user:loginWithEmailAndPassword"));

   try {
      const userCredential = yield* call(() =>
         signInWithEmailAndPassword(auth, action.payload.email, action.payload.password),
      );

      const user = userCredential.user;

      yield* put(userSlice.actions.setFirebaseUser(user));

      let userGetMeWithError = false;

      try {
         const response = yield* call(() => api("userGetMe"));

         yield* call(() => afterUserLoaded(response.data));
      } catch (error) {
         userGetMeWithError = true;
         yield* call(handleError, error);
      }

      if (!userGetMeWithError) {
         yield* call(() =>
            alertControls.createAlert({
               type: "success",
               title: i18n.t((l) => l.api.success.default),
               message: i18n.t((l) => l.api.success.list.userLoggedIn),
            }),
         );

         action.payload.onSuccess?.({
            firebaseUser: user,
         });
      }
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:loginWithEmailAndPassword"));
   }
}

function* onLoginWithProvider(action: ReturnType<typeof userSlice.actions.loginWithProvider>) {
   yield* call(() => loaderControls.startLoading("user:loginWithProvider"));

   try {
      const userCredential = yield* call(() => signInWithPopupProvider(action.payload.provider));

      const user = userCredential.user;

      yield* put(userSlice.actions.setFirebaseUser(user));

      let userGetMeWithError = false;

      try {
         const response = yield* call(() => api("userGetMe"));

         yield* call(() => afterUserLoaded(response.data));
      } catch (error) {
         if (isApiError(error)) {
            if (error.statusCode === 404) {
               yield* call(() =>
                  finishRegister({
                     fullName: user.displayName ?? user.email?.split("@")[0] ?? "",
                  }),
               );
            } else {
               userGetMeWithError = true;
               yield* call(handleError, error);
            }
         } else {
            userGetMeWithError = true;
            yield* call(handleError, error);
         }
      }

      if (!userGetMeWithError) {
         yield* call(() =>
            alertControls.createAlert({
               type: "success",
               title: i18n.t((l) => l.api.success.default),
               message: i18n.t((l) => l.api.success.list.userLoggedIn),
            }),
         );

         action.payload.onSuccess?.({
            firebaseUser: user,
         });
      }
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:loginWithProvider"));
   }
}

function* onLoadUser() {
   const user = yield* select((state: RootState) => state.user.user);

   if (!user) {
      yield* put(
         userSlice.actions.logout({
            doNotInformUser: true,
         }),
      );
      return;
   }

   yield* call(() => loaderControls.startLoading("user:loadUser"));

   try {
      const response = yield* call(() => api("userGetMe"));

      yield* call(() => afterUserLoaded(response.data));
   } catch (error) {
      if (isApiError(error)) {
         if (error.statusCode === 404) {
            yield* put(
               userSlice.actions.logout({
                  doNotInformUser: true,
               }),
            );
         } else yield* call(handleError, error);
      } else yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:loadUser"));
   }
}

function* onLogout(action: ReturnType<typeof userSlice.actions.logout>) {
   yield* call(() => loaderControls.startLoading("user:logout"));

   try {
      yield* call(() => signOut(auth));

      yield* put(appSlice.actions.resetAppState());
      yield* call(() => LocalStorage.removeAllItems());

      if (!action.payload.doNotInformUser) {
         yield* call(() =>
            alertControls.createAlert({
               type: "success",
               title: i18n.t((l) => l.api.success.default),
               message: i18n.t((l) => l.api.success.list.userLoggedOut),
            }),
         );
      }

      router.navigate("/auth");
   } catch (error) {
      yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:logout"));
   }
}

function* onDeleteFirebaseUser(action: ReturnType<typeof userSlice.actions.deleteFirebaseUser>) {
   yield* call(() => loaderControls.startLoading("user:deleteFirebaseUser"));

   try {
      const userCredential = yield* call(() =>
         signInWithEmailAndPassword(auth, action.payload.email, action.payload.password),
      );

      yield* call(() => deleteUser(userCredential.user));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.accountDeleted),
         }),
      );

      yield* put(appSlice.actions.resetAppState());

      action.payload.onSuccess?.();
   } catch (error: any) {
      action.payload.onError?.(error);

      const errorCode = error.code;

      if (errorCode === "auth/requires-recent-login") {
         yield* call(() =>
            alertControls.createAlert({
               type: "warning",
               title: i18n.t((l) => l.api.warnings.default),
               message: i18n.t((l) => l.api.warnings.list.logInAgainToDeleteAccount),
            }),
         );
      } else yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:deleteFirebaseUser"));
   }
}

function* onSetFirebaseUser(action: ReturnType<typeof userSlice.actions.setFirebaseUser>) {
   yield* call(() =>
      action.payload ? LocalStorage.setItem("firebaseUser", action.payload) : LocalStorage.removeItem("firebaseUser"),
   );
}

function* onSetFirebaseUserIdToken(action: ReturnType<typeof userSlice.actions.setFirebaseUserIdToken>) {
   yield* call(() =>
      action.payload
         ? LocalStorage.setItem("firebaseUserIdToken", action.payload)
         : LocalStorage.removeItem("firebaseUserIdToken"),
   );
}

function* onSetUser(action: ReturnType<typeof userSlice.actions.setUser>) {
   yield* call(() => (action.payload ? LocalStorage.setItem("user", action.payload) : LocalStorage.removeItem("user")));
}

function* onSendEmailVerification() {
   const firebaseUser = yield* select((state: RootState) => state.user.firebaseUser);

   if (!firebaseUser) {
      yield* call(() =>
         alertControls.createAlert({
            type: "error",
            title: i18n.t((l) => l.api.errors.default),
            message: i18n.t((l) => l.api.errors.list.cannotSendEmailVerification),
         }),
      );
      return;
   }

   yield* call(() => loaderControls.startLoading("user:sendEmailVerification"));

   try {
      yield* call(() => sendEmailVerification(firebaseUser));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            message: i18n.t((l) => l.api.success.list.emailVerificationSent),
         }),
      );

      capturePosthogEvent("user:sendEmailVerification", {
         firebaseUserUid: firebaseUser.uid,
      });
   } catch (error) {
      yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:sendEmailVerification"));
   }
}

function* onEditFirebaseUserData(action: ReturnType<typeof userSlice.actions.editFirebaseUserData>) {
   const firebaseUser = yield* select((state: RootState) => state.user.firebaseUser);

   if (!firebaseUser) {
      yield* call(() =>
         alertControls.createAlert({
            type: "error",
            title: i18n.t((l) => l.api.errors.default),
            message: i18n.t((l) => l.api.errors.list.cannotUpdateUserData),
         }),
      );
      return;
   }

   yield* call(() => loaderControls.startLoading("user:editFirebaseUserData"));

   try {
      yield* call(() =>
         updateProfile(firebaseUser, {
            displayName: action.payload.displayName,
            photoURL: action.payload.photoURL,
         }),
      );

      yield* put(userSlice.actions.setFirebaseUser(firebaseUser));

      capturePosthogEvent("user:editFirebaseUserData", {
         firebaseUserUid: firebaseUser.uid,
      });

      action.payload.onSuccess?.();
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:editFirebaseUserData"));
   }
}

function* onEditUserData(action: ReturnType<typeof userSlice.actions.editUserData>) {
   yield* call(() => loaderControls.startLoading("user:editUserData"));

   try {
      const response = yield* call(() =>
         api("editUser", {
            body: action.payload.data,
         }),
      );

      yield* put(userSlice.actions.setUser(response.data));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            message: i18n.t((l) => l.api.success.list.userUpdated),
         }),
      );

      action.payload.onSuccess?.(response.data);
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:editUserData"));
   }
}

function* onEditFirebaseUserProfileImage(action: ReturnType<typeof userSlice.actions.editFirebaseUserProfileImage>) {
   const firebaseUser = yield* select((state: RootState) => state.user.firebaseUser);

   if (!firebaseUser) {
      yield* call(() =>
         alertControls.createAlert({
            type: "error",
            title: i18n.t((l) => l.api.errors.list.cannotUpdateUserData),
            message: i18n.t((l) => l.api.errors.list.cannotUpdateUserDataDescription),
         }),
      );
      return;
   }

   yield* call(() => loaderControls.startLoading("user:editFirebaseUserProfileImage"));

   try {
      if (action.payload.file) {
         const uploadResponse = yield* call(() =>
            uploadBytes(ref(storageUserProfileImageRef, firebaseUser.uid), action.payload.file!, {
               contentType: action.payload.file?.type,
               customMetadata: action.payload.file
                  ? {
                       name: action.payload.file.name,
                    }
                  : undefined,
            }),
         );

         const downloadURL = yield* call(() => getDownloadURL(uploadResponse.ref));

         yield* call(() =>
            updateProfile(firebaseUser, {
               photoURL: downloadURL,
            }),
         );

         yield* put(
            userSlice.actions.editUserData({
               data: {
                  imageUrl: downloadURL,
               },
            }),
         );
      } else {
         yield* call(() => deleteObject(ref(storageUserProfileImageRef, firebaseUser.uid)));

         const googleProvider = firebaseUser.providerData.find((provider) => provider.providerId === "google.com");

         yield* call(() =>
            updateProfile(firebaseUser, {
               photoURL: googleProvider?.photoURL ?? "",
            }),
         );

         yield* put(
            userSlice.actions.editUserData({
               data: {
                  imageUrl: null,
               },
            }),
         );
      }

      yield* put(userSlice.actions.setFirebaseUser(firebaseUser));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: i18n.t((l) => l.api.success.list.profileImageUpdated),
         }),
      );

      capturePosthogEvent("user:editFirebaseUserProfileImage", {
         firebaseUserUid: firebaseUser.uid,
      });

      action.payload.onSuccess?.();
   } catch (error) {
      action.payload.onError?.(error);
      yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:editFirebaseUserProfileImage"));
   }
}

function* onChangePassword(action: ReturnType<typeof userSlice.actions.changePassword>) {
   const firebaseUser = yield* select((state: RootState) => state.user.firebaseUser);

   if (!firebaseUser) {
      yield* call(() =>
         alertControls.createAlert({
            type: "warning",
            title: i18n.t((l) => l.api.warnings.default),
            message: i18n.t((l) => l.api.warnings.list.cannotChangePassword),
         }),
      );
      return;
   }

   yield* call(() => loaderControls.startLoading("user:changePassword"));

   try {
      const userCredential = action.payload.currentPassword
         ? yield* call(() =>
              signInWithEmailAndPassword(auth, firebaseUser.email ?? "", action.payload.currentPassword ?? ""),
           )
         : undefined;

      const maybeNewUser = userCredential ? userCredential.user : firebaseUser;

      yield* call(() => updatePassword(maybeNewUser, action.payload.newPassword));

      if (auth.currentUser) yield* put(userSlice.actions.setFirebaseUser({ ...auth.currentUser }));

      yield* call(() =>
         alertControls.createAlert({
            type: "success",
            title: i18n.t((l) => l.api.success.default),
            message: action.payload.currentPassword
               ? i18n.t((l) => l.api.success.list.passwordChanged)
               : i18n.t((l) => l.api.success.list.passwordSet),
         }),
      );

      capturePosthogEvent("user:changePassword", {
         firebaseUserUid: firebaseUser.uid,
      });

      action.payload.onSuccess?.();
   } catch (error: any) {
      action.payload.onError?.(error);

      const errorCode = error.code;

      if (errorCode === "auth/requires-recent-login") {
         yield* call(() =>
            alertControls.createAlert({
               type: "warning",
               title: i18n.t((l) => l.api.warnings.default),
               message: i18n.t((l) => l.api.warnings.list.logInAgainToChangePassword),
            }),
         );
      } else yield* call(handleFirebaseError, error);
   } finally {
      yield* call(() => loaderControls.stopLoading("user:changePassword"));
   }
}

function* afterUserLoaded(user: User) {
   yield* put(userSlice.actions.setUser(user));
   yield* put(userSlice.actions.setUserLoaded(true));
   yield* put(appSlice.actions.loadUserRelatedData());
}

export default function* userSagas() {
   yield* all([
      takeLatest(userSlice.actions.signUpWithEmailAndPassword, onSignUpWithEmailAndPassword),
      takeLatest(userSlice.actions.loginWithEmailAndPassword, onLoginWithEmailAndPassword),
      takeLatest(userSlice.actions.loginWithProvider, onLoginWithProvider),
      takeLatest(userSlice.actions.loadUser, onLoadUser),
      takeLatest(userSlice.actions.logout, onLogout),
      takeLatest(userSlice.actions.deleteFirebaseUser, onDeleteFirebaseUser),
      takeLatest(userSlice.actions.setFirebaseUser, onSetFirebaseUser),
      takeLatest(userSlice.actions.setFirebaseUserIdToken, onSetFirebaseUserIdToken),
      takeLatest(userSlice.actions.setUser, onSetUser),
      takeLatest(userSlice.actions.sendEmailVerification, onSendEmailVerification),
      takeLatest(userSlice.actions.editFirebaseUserData, onEditFirebaseUserData),
      takeLatest(userSlice.actions.editUserData, onEditUserData),
      takeLatest(userSlice.actions.editFirebaseUserProfileImage, onEditFirebaseUserProfileImage),
      takeLatest(userSlice.actions.changePassword, onChangePassword),
   ]);
}
